import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Dialog, DialogContent, Typography } from "@mui/material";
import { launchAudit, unwrapPost } from "../../../../helpers/auditHub_helper";
import { AH_ACCENT, AH_ACCENT_HOVER, AH_TEXT, AH_WHITE, auditHubPageSx } from "../theme";

const dialogButtonSx = {
  minWidth: 140,
  height: 38,
  px: 2,
  borderRadius: "4px",
  textTransform: "none",
  fontWeight: 400,
  fontSize: "0.875rem",
  boxShadow: "none",
  "&:hover": { boxShadow: "none" },
};

/**
 * `beforeLaunch`, when given, runs first (e.g. writing unsaved edits); a failure stops the launch.
 * An assessment that does not exist yet has no `auditId`; its caller passes `onConfirm` and reports
 * progress and failure through `busy` and `errorText`.
 */
const LaunchAuditDialog = ({ audit, open, onClose, onLaunched, beforeLaunch, onConfirm, busy = false, errorText = "" }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setSaving(false);
    setError("");
  }, [open, audit?.auditId]);

  const confirm = async () => {
    if (saving || busy) return;
    if (onConfirm) {
      onConfirm();
      return;
    }
    if (!audit?.auditId) return;
    setSaving(true);
    setError("");
    try {
      if (beforeLaunch) await beforeLaunch();
      const launched = unwrapPost(await launchAudit(audit.auditId));
      const failures = Array.isArray(launched?.scanDispatchFailures)
        ? launched.scanDispatchFailures.filter(Boolean)
        : [];
      onLaunched(audit, failures);
    } catch (err) {
      setError(err?.message || "Unable to launch this assessment.");
    } finally {
      setSaving(false);
    }
  };

  const label = [audit?.auditCode, audit?.name].filter(Boolean).join(" · ");
  const pending = saving || busy;
  const shownError = error || errorText;

  return (
    <Dialog
      open={open}
      onClose={pending ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="launch-audit-title"
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      PaperProps={{ sx: { ...auditHubPageSx, borderRadius: "12px", overflow: "hidden", maxWidth: 600 } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          minHeight: 56,
          px: 3,
          backgroundColor: AH_ACCENT,
        }}
      >
        <Typography id="launch-audit-title" sx={{ fontWeight: 700, fontSize: "1.0625rem", color: AH_WHITE, lineHeight: 1.3 }}>
          Launch Assessment
        </Typography>
        <Box
          component="button"
          type="button"
          aria-label="Close"
          disabled={pending}
          onClick={onClose}
          sx={{
            p: 0,
            m: 0,
            border: "none",
            background: "transparent",
            color: AH_WHITE,
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            cursor: pending ? "default" : "pointer",
          }}
        >
          ×
        </Box>
      </Box>
      <DialogContent sx={{ px: 3, py: 2.5, backgroundColor: AH_WHITE }}>
        <Typography sx={{ fontSize: "0.875rem", color: AH_TEXT, lineHeight: 1.6, overflowWrap: "anywhere" }}>
          Are you sure you want to launch{" "}
          {label ? (
            <Box component="span" sx={{ fontWeight: 600 }}>
              {label}
            </Box>
          ) : (
            "this assessment"
          )}
          ?
        </Typography>
        <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", lineHeight: 1.6, mt: 1 }}>
          {audit?.auditId
            ? `Confirm Launch of ${audit.auditCode || "this assessment"}. Launching will move this assessment from Draft to In-Progress and trigger automated scans for all associated accounts.`
            : "Confirm Launch of this assessment. Launching will create it as In-Progress and trigger automated scans for all associated accounts."}
        </Typography>
      </DialogContent>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: { xs: "wrap", sm: "nowrap" },
          gap: 1.5,
          px: 3,
          py: 2,
          borderTop: "1px solid #e5e7eb",
          backgroundColor: "#f8fafc",
        }}
      >
        <Button
          onClick={onClose}
          disabled={pending}
          sx={{
            ...dialogButtonSx,
            backgroundColor: "#74788d",
            color: "#eff2f7",
            "&:hover": { backgroundColor: "#636678", boxShadow: "none" },
          }}
        >
          Cancel
        </Button>
        {shownError ? (
          <Typography
            role="alert"
            sx={{
              flex: 1,
              minWidth: 0,
              order: { xs: 3, sm: 0 },
              flexBasis: { xs: "100%", sm: "auto" },
              fontSize: "0.8125rem",
              lineHeight: 1.45,
              color: "#dc2626",
              textAlign: "center",
              overflowWrap: "anywhere",
            }}
          >
            {shownError}
          </Typography>
        ) : null}
        <Button
          onClick={confirm}
          disabled={pending || (!onConfirm && !audit?.auditId)}
          startIcon={pending ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            ...dialogButtonSx,
            backgroundColor: AH_ACCENT,
            color: AH_WHITE,
            "&:hover": { backgroundColor: AH_ACCENT_HOVER, boxShadow: "none" },
            "&.Mui-disabled": { backgroundColor: AH_ACCENT, color: AH_WHITE, opacity: 0.65 },
          }}
        >
          {pending ? "Launching" : "Launch"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default LaunchAuditDialog;
